import _ from 'lodash';
import * as moment from 'moment-timezone';

import { Component, OnInit, forwardRef, Input, OnDestroy, SimpleChanges, DestroyRef, inject  } from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, ControlValueAccessor, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MdbDatepickerModule } from 'mdb-angular-ui-kit/datepicker';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { MdbTimepickerModule } from 'mdb-angular-ui-kit/timepicker';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MdbFormsModule,MdbTimepickerModule, MdbDatepickerModule,DatepickerComponent],
  selector: 'app-datetime',
  templateUrl: './datetime.component.html',
  styleUrls: ['./datetime.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatetimeComponent),
    multi: true,
  }],
})
export class DatetimeComponent implements OnInit, OnDestroy, ControlValueAccessor {
  destroyRef = inject(DestroyRef)
  @Input() dateLabel: string = null;
  @Input() timeLabel: string = null;
  @Input() timezone: string = moment.tz.guess();

  @Input() myDatePickerOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  };

  @Input() required: boolean = false;
  @Input() submitted: boolean = false;

  public id: number = null;
  public form: UntypedFormGroup = null;

  constructor(
    private fb: UntypedFormBuilder
  ) {
    this.form = this.fb.group({
      'date': new UntypedFormControl(null, this.required ? [Validators.required]: []),
      'time': new UntypedFormControl(null, this.required ? [Validators.required]: []),
      'timestamp': new UntypedFormControl(null, [])
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      'date': new UntypedFormControl(null, this.required ? [Validators.required]: []),
      'time': new UntypedFormControl(null, this.required ? [Validators.required]: []),
      'timestamp': new UntypedFormControl(null, [])
    });
    this.form.valueChanges.pipe(
      map(value => {
        const date = _.get(value, 'date', null);
        return { ...value, date };
      }),
      filter(({ date }) => !_.isNull(date)),
      filter(({ time }) => !_.isNull(time)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({date, time}) => {
      const timestamp = moment.tz(`${date},${time}`, 'MM/DD/YYYY,hh:mmA', this.timezone).unix();
      this.form.get('timestamp').setValue(timestamp, { emitEvent: false });

      const value = this.required ? null: 0;
      this.onChangeCallback(_.isNaN(timestamp) ? value: timestamp);
    });

    this.form.valueChanges.pipe(
      map(value => {
        const date = _.get(value, 'date', null);
        return { ...value, date };
      }),
      filter(({ date }) => _.isNull(date)),
      filter(({ time }) => !_.isNull(time)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.onChangeCallback(null);
    });

    this.form.valueChanges.pipe(
      map(value => {
        const date = _.get(value, 'date', null);
        return { ...value, date };
      }),
      filter(({ date }) => !_.isNull(date)),
      filter(({ time }) => _.isNull(time)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.onChangeCallback(null);
    });

    this.form.valueChanges.pipe(
      map(value => {
        const date = _.get(value, 'date', null);
        return { ...value, date };
      }),
      filter(({ date }) => _.isNull(date)),
      filter(({ time }) => _.isNull(time)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.onChangeCallback(this.required ? null: 0);
    });

    this.form.valueChanges.pipe(
      map(value => {
        const date = _.get(value, 'date', null);
        return { ...value, date };
      }),
      filter(({ date }) => _.isNull(date)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.form.get('time').setValidators(this.required ? [Validators.required]: []);
      this.form.get('time').updateValueAndValidity({ emitEvent: false });
    });

    this.form.valueChanges.pipe(
      map(value => {
        const date = _.get(value, 'date', null);
        return { ...value, date };
      }),
      filter(({ date }) => !_.isNull(date)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.form.get('time').setValidators([Validators.required]);
      this.form.get('time').updateValueAndValidity({ emitEvent: false });
    });

    this.form.valueChanges.pipe(
      filter(({ time }) => _.isNull(time)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.form.get('date').setValidators(this.required ? [Validators.required]: []);
      this.form.get('date').updateValueAndValidity({ emitEvent: false });
    });

    this.form.valueChanges.pipe(
      filter(({ time }) => !_.isNull(time)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.form.get('date').setValidators([Validators.required]);
      this.form.get('date').updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { form } = this;

    const submitted = _.get(changes, 'submitted.currentValue', this.submitted);
    if(submitted) {
      this.form.get('date').markAllAsTouched();
      this.form.get('time').markAllAsTouched();

      this.form.get('date').updateValueAndValidity();
      this.form.get('time').updateValueAndValidity();
    }

    const timezone = _.get(changes, 'timezone.currentValue', this.timezone);

    const date = form.get('date').value;
    const time = form.get('time').value;

    if(form.get('date').valid && form.get('time').valid) {
      const timestamp = moment.tz(`${date},${time}`, 'MM/DD/YYYY,hh:mmA', timezone).unix();
      const timestampControl = form.get('timestamp');
      if(timestampControl.value !== timestamp) {
        form.get('timestamp').setValue(timestamp, { emitEvent: false });
        const value = this.required ? null: 0;
        this.onChangeCallback(_.isNaN(timestamp) ? value: timestamp);
      }
    }
  }

  ngOnDestroy(): void {
  }

  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  writeValue(timestamp) {
    if(_.isNull(timestamp)) {
      return;
    }

    const unixObject = moment.tz(timestamp * 1000, this.timezone);
    const year = unixObject.year();
    if(year < 2016 || year > 2065) {
      this.form.get('date').setValue(null);
      this.form.get('time').setValue(null);
      this.form.get('timestamp').setValue(null);
      return;
    }

    const dateTimeObject = moment.tz(timestamp * 1000, this.timezone).format('MM/DD/YYYY,hh:mm A');
    const dateTimeArray = _.split(dateTimeObject, ',');

    this.form.get('date').setValue(dateTimeArray[0]);
    this.form.get('time').setValue(dateTimeArray[1]);
    this.form.get('timestamp').setValue(timestamp);
  }

  updateValue(timestamp, timezone) {
    if(_.isNull(timestamp)) {
      return;
    }

    const unixObject = moment.tz(timestamp * 1000, timezone);
    const year = unixObject.year();
    if(year < 2016 || year > 2065) {
      this.form.get('date').setValue(null);
      this.form.get('time').setValue(null);
      this.form.get('timestamp').setValue(null);
      return;
    }

    const dateTimeObject = moment.tz(timestamp * 1000, timezone).format('MM/DD/YYYY,hh:mm A');
    const dateTimeArray = _.split(dateTimeObject, ',');

    this.form.get('date').setValue(dateTimeArray[0]);
    this.form.get('time').setValue(dateTimeArray[1]);
    this.form.get('timestamp').setValue(timestamp);
  }

  onTimeChange(value) {
    this.form.get('time').setValue(value);
  }

  onClear() {
    this.form.get('time').setValue(null);
  }

}
